import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { OpeningLayoutBounds } from "building-types/framing/framingLayoutTypes";
import type { FramingProfileDescriptor } from "building-types/framing/framingProfileDescriptorTypes";
import type {
  BandFramingLayoutContext,
  WallOpeningHorizontalBand,
} from "building-types/framing/wallFramingOpeningClusterTypes";
import type {
  BandFramingOptions,
  OpeningFramingConfigResolver,
} from "building-types/framing/wallFramingOpeningConfigTypes";
import type {
  GeneratedWallOpeningMember,
  OpeningFramingExtents,
  ValidatedWallOpening,
  WallOpeningFramingContext,
} from "building-types/framing/wallFramingOpeningTypes";
import { WallOpeningFramingMode } from "building-types/framing/wallFramingOpeningTypes";
import { DEFAULT_STACKED_OPENING_HEADER_SILL_MERGE_CLEARANCE_INCHES } from "building-types/framing/wallFramingTypes";
import { WallFramingType } from "building-types/framing/wallFramingTypes";
import type { Table } from "common/data/table";
import { DIST_EPSILON } from "geom/core";
import {
  bandBottomCrippleObstructingStudPerps,
  createBandJackKingResolution,
  resolveOpeningFramingConfigForBand,
} from "./wallFramingOpeningConfig";
import {
  addWallCrippleMembersInPerpSpan,
  computeOpeningFramingExtents,
  framingExtentsPerpOverlap,
  generateWallOpeningFramingMembers,
  openingSillBottomParallel,
  verticallyAdjacentForHeaderSillMerge,
} from "./wallFramingOpenings";
import { roughOpeningEndPerp, roughOpeningStartPerp } from "./wallFramingUtils";

export function resolveStackedOpeningHeaderSillMergeClearance(clearance: number | undefined): number {
  return clearance ?? DEFAULT_STACKED_OPENING_HEADER_SILL_MERGE_CLEARANCE_INCHES;
}

/**helper function to add opening indices metadata to members */
function tagWithOpeningIndices(
  members: GeneratedWallOpeningMember[],
  openingIndices: readonly number[],
): GeneratedWallOpeningMember[] {
  return members.map((member) => ({ ...member, openingIndices }));
}

/**helper function to add opening indices metadata to members from start index onward */
function tagOpeningMembersFromStartIndex(
  members: GeneratedWallOpeningMember[],
  startIndex: number,
  openingIndices: readonly number[],
): void {
  for (let i = startIndex; i < members.length; i++) {
    members[i] = { ...members[i], openingIndices };
  }
}

function framingExtentsOverlap(a: OpeningFramingExtents, b: OpeningFramingExtents): boolean {
  return a.startPerp < b.endPerp - DIST_EPSILON && b.startPerp < a.endPerp - DIST_EPSILON;
}

function openingRoughPerpOverlap(a: ValidatedWallOpening, b: ValidatedWallOpening): boolean {
  return a.bounds.startPerp < b.bounds.endPerp - DIST_EPSILON && b.bounds.startPerp < a.bounds.endPerp - DIST_EPSILON;
}

function openingsOverlapForBandGrouping(
  openingA: ValidatedWallOpening,
  openingB: ValidatedWallOpening,
  extentsA: OpeningFramingExtents,
  extentsB: OpeningFramingExtents,
  mergeJackKing: boolean,
): boolean {
  if (mergeJackKing) {
    return openingRoughPerpOverlap(openingA, openingB);
  }
  return framingExtentsOverlap(extentsA, extentsB);
}

export function createBandFramingLayoutContext(
  band: WallOpeningHorizontalBand,
  resolveOpeningConfig: OpeningFramingConfigResolver,
  bandFramingOptions: BandFramingOptions,
  defaultProfile: FramingProfileDescriptor,
  framingComponents: Table<FramingComponentEntity>,
  plateThickness: number,
): BandFramingLayoutContext {
  const jackKingResolution = createBandJackKingResolution(band.openings, resolveOpeningConfig, bandFramingOptions);
  const extentsByOpeningIndex = new Map<number, OpeningFramingExtents>();
  let combinedStartPerp = Infinity;
  let combinedEndPerp = -Infinity;

  for (const opening of band.openings) {
    const configGroup = resolveOpeningFramingConfigForBand(
      opening.openingIndex,
      resolveOpeningConfig,
      jackKingResolution,
    );
    if (!configGroup) {
      continue;
    }
    const extents = computeOpeningFramingExtents(
      opening,
      configGroup,
      defaultProfile,
      framingComponents,
      plateThickness,
    );
    extentsByOpeningIndex.set(opening.openingIndex, extents);
    combinedStartPerp = Math.min(combinedStartPerp, extents.startPerp);
    combinedEndPerp = Math.max(combinedEndPerp, extents.endPerp);
  }

  const combinedPerpExtent =
    combinedStartPerp !== Infinity
      ? { startPerp: combinedStartPerp, endPerp: combinedEndPerp }
      : band.combinedPerpExtent;

  return { jackKingResolution, extentsByOpeningIndex, combinedPerpExtent };
}

function findUnionParent(parents: number[], index: number): number {
  if (parents[index] !== index) {
    parents[index] = findUnionParent(parents, parents[index]);
  }
  return parents[index];
}

function unionIndices(parents: number[], a: number, b: number): void {
  const rootA = findUnionParent(parents, a);
  const rootB = findUnionParent(parents, b);
  if (rootA !== rootB) {
    parents[rootB] = rootA;
  }
}

export function groupWallOpeningsIntoHorizontalBands(
  validOpenings: readonly ValidatedWallOpening[],
  resolveOpeningConfig: OpeningFramingConfigResolver,
  defaultProfile: FramingProfileDescriptor,
  framingComponents: Table<FramingComponentEntity>,
  plateThickness: number,
  bandFramingOptions: BandFramingOptions,
): WallOpeningHorizontalBand[] {
  if (validOpenings.length === 0) {
    return [];
  }

  const extentsByOpeningIndex = new Map<number, OpeningFramingExtents>();
  for (const opening of validOpenings) {
    const configGroup = resolveOpeningConfig(opening.openingIndex);
    if (!configGroup) {
      continue;
    }
    extentsByOpeningIndex.set(
      opening.openingIndex,
      computeOpeningFramingExtents(opening, configGroup, defaultProfile, framingComponents, plateThickness),
    );
  }

  const parents = validOpenings.map((_, index) => index);
  for (let i = 0; i < validOpenings.length; i++) {
    const extentsI = extentsByOpeningIndex.get(validOpenings[i].openingIndex);
    if (!extentsI) {
      continue;
    }
    for (let j = i + 1; j < validOpenings.length; j++) {
      const extentsJ = extentsByOpeningIndex.get(validOpenings[j].openingIndex);
      if (!extentsJ) {
        continue;
      }
      if (
        openingsOverlapForBandGrouping(
          validOpenings[i],
          validOpenings[j],
          extentsI,
          extentsJ,
          bandFramingOptions.mergeAlignedOpeningJackKingCounts,
        )
      ) {
        unionIndices(parents, i, j);
      }
    }
  }

  const bandsByRoot = new Map<number, ValidatedWallOpening[]>();
  for (let i = 0; i < validOpenings.length; i++) {
    const root = findUnionParent(parents, i);
    const list = bandsByRoot.get(root) ?? [];
    list.push(validOpenings[i]);
    bandsByRoot.set(root, list);
  }

  const bands: WallOpeningHorizontalBand[] = [];
  for (const openings of bandsByRoot.values()) {
    let combinedStartPerp = Infinity;
    let combinedEndPerp = -Infinity;
    let combinedRoStartPerp = Infinity;
    let combinedRoEndPerp = -Infinity;
    let combinedRoBottom = Infinity;
    let combinedRoTop = -Infinity;
    const openingIndex = openings[0].openingIndex;

    for (const opening of openings) {
      const extents = extentsByOpeningIndex.get(opening.openingIndex);
      const framingStartPerp = extents?.startPerp ?? opening.bounds.startPerp;
      const framingEndPerp = extents?.endPerp ?? opening.bounds.endPerp;
      combinedStartPerp = Math.min(combinedStartPerp, framingStartPerp);
      combinedEndPerp = Math.max(combinedEndPerp, framingEndPerp);
      combinedRoStartPerp = Math.min(combinedRoStartPerp, opening.bounds.startPerp);
      combinedRoEndPerp = Math.max(combinedRoEndPerp, opening.bounds.endPerp);
      combinedRoBottom = Math.min(combinedRoBottom, opening.bounds.bottomParallel);
      combinedRoTop = Math.max(combinedRoTop, opening.bounds.topParallel);
    }

    const band: WallOpeningHorizontalBand = {
      openings,
      combinedPerpExtent: { startPerp: combinedStartPerp, endPerp: combinedEndPerp },
      combinedRoughOpeningBounds: {
        openingIndex,
        startPerp: combinedRoStartPerp,
        endPerp: combinedRoEndPerp,
        bottomParallel: combinedRoBottom,
        topParallel: combinedRoTop,
      },
    };
    const layoutContext = createBandFramingLayoutContext(
      band,
      resolveOpeningConfig,
      bandFramingOptions,
      defaultProfile,
      framingComponents,
      plateThickness,
    );
    bands.push({ ...band, combinedPerpExtent: layoutContext.combinedPerpExtent });
  }

  return bands;
}

function sortOpeningsByBottomParallel(openings: readonly ValidatedWallOpening[]): ValidatedWallOpening[] {
  return [...openings].sort((a, b) => a.bounds.bottomParallel - b.bounds.bottomParallel);
}

function computeJackBottomParallel(
  openingIndexInBand: number,
  sortedOpenings: readonly ValidatedWallOpening[],
  extentsByOpeningIndex: ReadonlyMap<number, OpeningFramingExtents>,
  studBottomParallel: number,
): number {
  if (openingIndexInBand === 0) {
    return studBottomParallel;
  }
  const previousExtents = extentsByOpeningIndex.get(sortedOpenings[openingIndexInBand - 1].openingIndex);
  if (previousExtents) {
    return previousExtents.headerTopParallel;
  }
  return studBottomParallel;
}

function resolveBandKingOpening(band: WallOpeningHorizontalBand): ValidatedWallOpening {
  const hasDoor = band.openings.some((opening) => opening.mode === WallOpeningFramingMode.DOOR);
  return {
    openingIndex: band.combinedRoughOpeningBounds.openingIndex,
    bounds: band.combinedRoughOpeningBounds,
    mode: hasDoor ? WallOpeningFramingMode.DOOR : WallOpeningFramingMode.WINDOW,
  };
}

export function generateWallOpeningFramingForBand(
  band: WallOpeningHorizontalBand,
  resolveOpeningConfig: OpeningFramingConfigResolver,
  ctx: WallOpeningFramingContext,
  framingComponents: Table<FramingComponentEntity>,
  defaultProfile: FramingProfileDescriptor,
  bandFramingOptions: BandFramingOptions,
): GeneratedWallOpeningMember[] {
  const plateThickness = defaultProfile.height;
  const layoutContext = createBandFramingLayoutContext(
    band,
    resolveOpeningConfig,
    bandFramingOptions,
    defaultProfile,
    framingComponents,
    plateThickness,
  );
  const { jackKingResolution, extentsByOpeningIndex } = layoutContext;

  const sorted = sortOpeningsByBottomParallel(band.openings);
  const members: GeneratedWallOpeningMember[] = [];
  const kingOpening = resolveBandKingOpening(band);
  const kingFramingConfig = jackKingResolution.merged ?? resolveOpeningConfig(kingOpening.openingIndex);
  // King studs are shared across the whole band; tag with every opening index in the band.
  const bandOpeningIndices = band.openings.map((bandOpening) => bandOpening.openingIndex);

  if (kingFramingConfig) {
    members.push(
      ...tagWithOpeningIndices(
        generateWallOpeningFramingMembers(kingOpening, kingFramingConfig, ctx, framingComponents, defaultProfile, {
          includeKings: true,
          includeJacks: false,
          includeHeaders: false,
          includeHeaderSills: false,
          includeSills: false,
          includeCripples: false,
        }),
        bandOpeningIndices,
      ),
    );
  }

  for (let i = 0; i < sorted.length; i++) {
    const opening = sorted[i];
    const framingConfig = resolveOpeningFramingConfigForBand(
      opening.openingIndex,
      resolveOpeningConfig,
      jackKingResolution,
    );
    if (!framingConfig) {
      continue;
    }
    const jackBottomParallel = computeJackBottomParallel(i, sorted, extentsByOpeningIndex, ctx.studBottomParallel);
    const useAssemblyLayout = band.openings.length > 1;
    members.push(
      ...tagWithOpeningIndices(
        generateWallOpeningFramingMembers(opening, framingConfig, ctx, framingComponents, defaultProfile, {
          includeKings: false,
          includeJacks: true,
          includeHeaders: true,
          includeSills: true,
          includeCripples: false,
          jackBottomParallel,
          assemblyLayoutBounds: useAssemblyLayout ? band.combinedRoughOpeningBounds : undefined,
        }),
        [opening.openingIndex],
      ),
    );
  }

  const topmost = sorted[sorted.length - 1];
  const topmostConfig = resolveOpeningFramingConfigForBand(
    topmost.openingIndex,
    resolveOpeningConfig,
    jackKingResolution,
  );
  if (topmostConfig) {
    members.push(
      ...tagWithOpeningIndices(
        generateWallOpeningFramingMembers(topmost, topmostConfig, ctx, framingComponents, defaultProfile, {
          includeKings: false,
          includeJacks: false,
          includeHeaders: false,
          includeHeaderSills: false,
          includeSills: false,
          includeCripples: true,
        }),
        [topmost.openingIndex],
      ),
    );
  }

  generateBandStackedCrippleMembers(
    members,
    band,
    sorted,
    layoutContext,
    resolveOpeningConfig,
    ctx,
    framingComponents,
    defaultProfile,
    plateThickness,
  );

  return members;
}

function generateBandStackedCrippleMembers(
  members: GeneratedWallOpeningMember[],
  band: WallOpeningHorizontalBand,
  sorted: readonly ValidatedWallOpening[],
  layoutContext: BandFramingLayoutContext,
  resolveOpeningConfig: OpeningFramingConfigResolver,
  ctx: WallOpeningFramingContext,
  framingComponents: Table<FramingComponentEntity>,
  defaultProfile: FramingProfileDescriptor,
  plateThickness: number,
): void {
  if (sorted.length === 0) {
    return;
  }

  const { jackKingResolution, extentsByOpeningIndex } = layoutContext;
  const studThickness = defaultProfile.height;
  const bandBottomObstructingPerps = bandBottomCrippleObstructingStudPerps(band, jackKingResolution, studThickness);
  const useBandRoughOpeningSpan = band.openings.length > 1;

  const bottommost = sorted[0];
  const bottommostConfig = resolveOpeningFramingConfigForBand(
    bottommost.openingIndex,
    resolveOpeningConfig,
    jackKingResolution,
  );
  const bottommostSillBottom =
    bottommostConfig !== undefined
      ? openingSillBottomParallel(bottommost, bottommostConfig, plateThickness)
      : undefined;
  if (bottommostSillBottom !== undefined && bottommostConfig !== undefined) {
    const crippleBounds = useBandRoughOpeningSpan ? band.combinedRoughOpeningBounds : bottommost.bounds;
    const crippleStartIndex = members.length;
    addWallCrippleMembersInPerpSpan(
      members,
      roughOpeningEndPerp(crippleBounds),
      roughOpeningStartPerp(crippleBounds),
      ctx.studBottomParallel,
      bottommostSillBottom,
      bottommostConfig,
      ctx,
      framingComponents,
      defaultProfile,
      WallFramingType.BOTTOM_CRIPPLE_STUD,
      bandBottomObstructingPerps,
    );
    tagOpeningMembersFromStartIndex(members, crippleStartIndex, [bottommost.openingIndex]);
  }

  for (let i = 1; i < sorted.length; i++) {
    const lower = sorted[i - 1];
    const upper = sorted[i];
    const lowerExtents = extentsByOpeningIndex.get(lower.openingIndex);
    const upperExtents = extentsByOpeningIndex.get(upper.openingIndex);
    const upperConfig = resolveOpeningFramingConfigForBand(
      upper.openingIndex,
      resolveOpeningConfig,
      jackKingResolution,
    );
    const upperSillBottom =
      upperConfig !== undefined ? openingSillBottomParallel(upper, upperConfig, plateThickness) : undefined;
    if (!lowerExtents || !upperExtents || upperSillBottom === undefined || upperConfig === undefined) {
      continue;
    }

    const perpOverlap = framingExtentsPerpOverlap(lowerExtents, upperExtents);
    if (!perpOverlap) {
      continue;
    }

    // Cripples between two stacked openings belong to both the lower and upper opening.
    const crippleStartIndex = members.length;
    addWallCrippleMembersInPerpSpan(
      members,
      perpOverlap.minPerp,
      perpOverlap.maxPerp,
      lowerExtents.headerTopParallel,
      upperSillBottom,
      upperConfig,
      ctx,
      framingComponents,
      defaultProfile,
      WallFramingType.BOTTOM_CRIPPLE_STUD,
      bandBottomObstructingPerps,
    );
    tagOpeningMembersFromStartIndex(members, crippleStartIndex, [lower.openingIndex, upper.openingIndex]);
  }
}

/** Layout bounds used for stud clipping, merging parallel extents within vertically merged stacks. */
export function layoutBoundsForStudClippingInBand(
  band: WallOpeningHorizontalBand,
  mergeClearance: number,
  resolveOpeningConfig: OpeningFramingConfigResolver,
  defaultProfile: FramingProfileDescriptor,
  framingComponents: Table<FramingComponentEntity>,
  plateThickness: number,
  bandFramingOptions: BandFramingOptions,
): OpeningLayoutBounds[] {
  const sorted = sortOpeningsByBottomParallel(band.openings);
  const { extentsByOpeningIndex } = createBandFramingLayoutContext(
    band,
    resolveOpeningConfig,
    bandFramingOptions,
    defaultProfile,
    framingComponents,
    plateThickness,
  );

  const mergedBounds: OpeningLayoutBounds[] = [];
  let stackStart = 0;

  const flushStack = (stackEnd: number): void => {
    const stack = sorted.slice(stackStart, stackEnd + 1);
    let startPerp = Infinity;
    let endPerp = -Infinity;
    let bottomParallel = Infinity;
    let topParallel = -Infinity;
    for (const opening of stack) {
      const extents = extentsByOpeningIndex.get(opening.openingIndex);
      startPerp = Math.min(startPerp, extents?.startPerp ?? opening.bounds.startPerp);
      endPerp = Math.max(endPerp, extents?.endPerp ?? opening.bounds.endPerp);
      bottomParallel = Math.min(bottomParallel, opening.bounds.bottomParallel);
      topParallel = Math.max(topParallel, opening.bounds.topParallel);
    }
    mergedBounds.push({
      openingIndex: stack[0].openingIndex,
      startPerp,
      endPerp,
      bottomParallel,
      topParallel,
    });
  };

  for (let i = 0; i < sorted.length; i++) {
    if (i === 0) {
      continue;
    }
    const previous = sorted[i - 1];
    const current = sorted[i];
    const previousExtents = extentsByOpeningIndex.get(previous.openingIndex);
    const currentExtents = extentsByOpeningIndex.get(current.openingIndex);
    const verticallyMerged =
      previousExtents &&
      currentExtents &&
      verticallyAdjacentForHeaderSillMerge(previous, current, previousExtents, currentExtents, mergeClearance);

    if (!verticallyMerged) {
      flushStack(i - 1);
      stackStart = i;
    }
  }
  flushStack(sorted.length - 1);

  return mergedBounds;
}
